import { useEffect, useState, useMemo, useCallback } from "react";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Search, BookOpen, Wallet, Landmark, PiggyBank, HandCoins, Boxes, Building, Receipt, TrendingUp, Package, Users, Plus, Pencil, Trash2, X, Settings2, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";
import LoadingState from "../../components/ui/loading-state";

// Ikon & warna adalah HIASAN, jadi tetap di sini. Daftar kategorinya sendiri
// TIDAK — sejak 25 Agu 2026 ia datang dari `/finance/kategori-akun`, karena
// daftar hardcoded di berkas ini sudah menyimpang dari backend: empat kategori
// yang dipakai backend (piutang_supplier, dibayar_dimuka, pajak_masukan,
// pajak_keluaran) tidak pernah ada di sini, dan akun-akunnya tampil di
// kelompok "Lainnya" tanpa label sama sekali.
const IKON = { Wallet, Landmark, PiggyBank, HandCoins, Boxes, Building, Receipt, TrendingUp, Package, Users, BookOpen };
const WARNA = {
  kas: "emerald", bank: "sky", petty_cash: "purple", piutang: "amber",
  piutang_supplier: "amber", dibayar_dimuka: "slate", persediaan: "teal",
  pajak_masukan: "slate", penerimaan_tradein: "teal", aset_tetap: "indigo",
  utang: "rose", pajak_keluaran: "rose", modal: "slate",
  pendapatan: "emerald", hpp: "orange", beban: "rose",
};

const PROTECTED_CODES = ["1200", "2000", "3000", "3100", "3200", "3900"];
const ROLE_ATUR = ["finance_spv", "finance_ceo", "admin", "superadmin"];

export default function FinanceCOA() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [reg, setReg] = useState(null);      // { kategori, kelompok, arah, tidak_terdaftar }
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showKategori, setShowKategori] = useState(false);
  const [editing, setEditing] = useState(null);
  // Diputuskan SERVER (`GET /finance/coa/izin`), bukan daftar role yang disalin
  // ke JS: halaman ini terbuka bagi semua pemegang `finance_coa.view` (termasuk
  // staf & auditor), sedangkan PUT/DELETE-nya lebih sempit — tanpa ini tombol
  // Edit/Hapus muncul untuk orang yang pasti ditolak 403 (aturan 3 CLAUDE.md).
  // Bawaannya menutup (false) supaya tombol tidak sempat berkedip saat memuat.
  const [izin, setIzin] = useState({ boleh_tambah: false, boleh_edit: false, boleh_hapus: false });

  // Gerbang yang SAMA PERSIS dengan `_bukan_admin()` di kategori_akun.py —
  // tombol yang ujungnya 403 tidak boleh muncul (aturan 3 CLAUDE.md).
  const bisaAturKategori = ROLE_ATUR.includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([api.get("/finance/coa"), api.get("/finance/kategori-akun"), api.get("/finance/coa/izin")])
      .then(([a, k, i]) => {
        setAccounts(a.data || []); setReg(k.data || null);
        setIzin({ boleh_tambah: !!i.data?.boleh_tambah, boleh_edit: !!i.data?.boleh_edit,
                  boleh_hapus: !!i.data?.boleh_hapus });
      })
      .catch((e) => { setLoadError(e); toast.error("Gagal memuat COA"); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const metaPer = useMemo(() => {
    const m = {};
    (reg?.kategori || []).forEach(k => {
      m[k.key] = { ...k, icon: IKON[k.ikon] || BookOpen, color: WARNA[k.key] || "slate" };
    });
    return m;
  }, [reg]);

  const urutanKelompok = useMemo(
    () => [...(reg?.kelompok || []), "Lainnya"], [reg]);

  const grouped = useMemo(() => {
    const s = q.trim().toLowerCase();
    const filtered = accounts.filter(a =>
      !s || (a.name || "").toLowerCase().includes(s) || (a.code || "").toLowerCase().includes(s) || (a.category || "").toLowerCase().includes(s)
    );
    const map = {};
    filtered.forEach(a => {
      // Kategori yang tidak terdaftar tetap DITAMPILKAN, di kelompok "Lainnya",
      // bukan disembunyikan. Akun yang hilang dari layar adalah cara §32 lahir.
      const meta = metaPer[a.category] || { label: a.category || "(tanpa kategori)", kelompok: "Lainnya", icon: BookOpen, color: "slate" };
      const g = meta.kelompok || "Lainnya";
      if (!map[g]) map[g] = {};
      if (!map[g][a.category]) map[g][a.category] = { meta, accounts: [] };
      map[g][a.category].accounts.push(a);
    });
    return map;
  }, [accounts, q, metaPer]);

  const del = async (a) => {
    if (PROTECTED_CODES.includes(a.code)) return toast.error("Akun sistem tidak boleh dihapus");
    if (!window.confirm(`Hapus akun '${a.name}' (${a.code})?`)) return;
    try { const r = await api.delete(`/finance/coa/${a.id}`); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const liar = reg?.tidak_terdaftar || [];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-coa">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Fondasi Akuntansi</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Chart of Accounts</h1>
          <p className="text-sm text-slate-600 mt-1">Semua akun yang tersedia untuk mencatat transaksi. Saldo real-time.</p>
        </div>
        <div className="flex gap-2">
          {bisaAturKategori && (
            <button onClick={() => setShowKategori(true)} data-testid="coa-kelola-kategori" className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Kategori Akun
            </button>
          )}
          {izin.boleh_tambah && (
            <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="coa-new" className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Akun Baru
            </button>
          )}
        </div>
      </div>

      {liar.length > 0 && (
        <div data-testid="coa-kategori-liar" className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Ada akun dengan kategori yang tidak terdaftar: {liar.join(", ")}</p>
            <p className="text-amber-800 mt-0.5">
              Akun-akun ini muncul di kelompok "Lainnya" dan tidak punya baris sendiri di Neraca.
              Pindahkan akunnya ke kategori terdaftar, atau daftarkan kategorinya lewat tombol Kategori Akun.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cari kode, nama akun, atau kategori..."
            data-testid="coa-search"
            className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
          />
        </div>
      </div>

      <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={3} data-testid="loading-coa">
        <div className="space-y-6">
          {urutanKelompok.map(groupName => {
            const groupData = grouped[groupName];
            if (!groupData) return null;
            return (
              <div key={groupName}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 rounded-full bg-emerald-600" />
                  <h2 className="font-display font-bold text-lg text-slate-900">{groupName}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {Object.entries(groupData).map(([cat, { meta, accounts: accs }]) => {
                    const Icon = meta.icon || BookOpen;
                    const totalBal = accs.reduce((s, a) => s + (a.balance || 0), 0);
                    return (
                      <div key={cat} data-testid={`coa-cat-${cat}`} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-slate-100">
                          <div className={`w-9 h-9 rounded-lg grid place-items-center bg-${meta.color}-50 text-${meta.color}-600 shrink-0`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 truncate">{meta.label}</p>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">
                              {accs.length} akun · normal {meta.arah_normal || "?"}
                            </p>
                          </div>
                          <p className="text-sm font-display font-bold text-slate-700 shrink-0">{idr(totalBal)}</p>
                        </div>
                        <div className="space-y-1">
                          {accs.map(a => (
                            <div key={a.id} className="flex items-center gap-2 text-sm py-1.5 px-2 rounded hover:bg-slate-50 group">
                              <div className="min-w-0 flex-1">
                                <p className="text-slate-800 truncate">{a.name}</p>
                                <p className="text-[10px] text-slate-400 font-mono">{a.code}{a.brand ? ` · ${a.brand}` : ""}</p>
                              </div>
                              <p className={`text-xs font-mono font-semibold ${a.balance < 0 ? "text-rose-600" : "text-slate-700"}`}>{idr(a.balance || 0)}</p>
                              {/* Di layar kecil TIDAK ADA hover — `opacity-0` di sana
                                  membuat Edit/Hapus mustahil ditemukan sama sekali,
                                  dan itulah kenapa owner mengira fitur editnya belum
                                  ada (brief 4 Sep). Tetap muncul-saat-hover di desktop. */}
                              <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 flex gap-0.5">
                                {izin.boleh_edit && (
                                  <button onClick={() => { setEditing(a); setShowForm(true); }} data-testid={`coa-edit-${a.code}`} title="Edit" className="w-6 h-6 grid place-items-center text-slate-400 hover:text-slate-900 rounded hover:bg-slate-100"><Pencil className="w-3 h-3" /></button>
                                )}
                                {izin.boleh_hapus && !PROTECTED_CODES.includes(a.code) && (
                                  <button onClick={() => del(a)} data-testid={`coa-del-${a.code}`} title="Hapus" className="w-6 h-6 grid place-items-center text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50"><Trash2 className="w-3 h-3" /></button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </LoadingState>

      {showForm && <FormCOA editing={editing} kategori={reg?.kategori || []} onClose={() => { setShowForm(false); setEditing(null); load(); }} />}
      {showKategori && <KelolaKategori reg={reg} onClose={() => { setShowKategori(false); load(); }} />}
    </div>
  );
}

function FormCOA({ editing, kategori, onClose }) {
  const aktif = (kategori || []).filter(k => k.is_active !== false);
  // Akun yang kategorinya sudah dinonaktifkan / tidak terdaftar (halaman ini
  // bahkan punya peringatan "kategori liar" untuk mereka) tidak punya opsi yang
  // cocok di <select>. Tanpa baris ini, yang TERBACA staf adalah kategori
  // pertama di daftar sementara yang tersimpan di state masih kategori aslinya —
  // layar dan data bilang dua hal berbeda. Opsinya ditambahkan apa adanya dan
  // ditandai, bukan disembunyikan.
  const pilihan = (editing?.category && !aktif.some(k => k.key === editing.category))
    ? [...aktif, { key: editing.category, label: `${editing.category} (tidak terdaftar)` }]
    : aktif;
  const [f, setF] = useState({
    code: editing?.code || "",
    name: editing?.name || "",
    category: editing?.category || pilihan[0]?.key || "kas",
    brand: editing?.brand || "",
    cabang: editing?.cabang || "",
    is_active: editing?.is_active !== undefined ? editing.is_active : true,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.code.trim() || !f.name.trim()) return toast.error("Kode dan nama wajib diisi");
    setBusy(true);
    try {
      if (editing?.id) {
        const payload = { name: f.name, category: f.category, brand: f.brand || null, cabang: f.cabang || null, is_active: f.is_active };
        await api.put(`/finance/coa/${editing.id}`, payload);
        toast.success("Akun diperbarui ✓");
      } else {
        await api.post("/finance/coa", { code: f.code, name: f.name, category: f.category, brand: f.brand || null, cabang: f.cabang || null });
        toast.success("Akun ditambahkan ✓");
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const terpilih = pilihan.find(k => k.key === f.category);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">{editing ? "Edit Akun COA" : "Akun Baru"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <Fld label="Kode Akun">
            <input value={f.code} onChange={e => setF({ ...f, code: e.target.value })} disabled={!!editing} placeholder="Contoh: 1017" data-testid="coa-form-code" className="in" />
            {editing && <p className="text-[10px] text-slate-400 mt-1">Kode tidak bisa diubah</p>}
          </Fld>
          <Fld label="Nama Akun"><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Contoh: Kas Toko Bandung" data-testid="coa-form-name" className="in" /></Fld>
          <Fld label="Kategori">
            <select value={f.category} onChange={e => setF({ ...f, category: e.target.value })} data-testid="coa-form-category" className="in">
              {pilihan.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
            {terpilih && (
              <p className="text-[10px] text-slate-500 mt-1">
                {terpilih.kelompok} · saldo normal {terpilih.arah_normal}
                {terpilih.boleh_transaksi_kas ? " · bisa dipakai membayar" : ""}
              </p>
            )}
          </Fld>
          <div className="grid grid-cols-2 gap-2">
            <Fld label="Brand (opsional)"><input value={f.brand} onChange={e => setF({ ...f, brand: e.target.value })} className="in" /></Fld>
            <Fld label="Cabang (opsional)"><input value={f.cabang} onChange={e => setF({ ...f, cabang: e.target.value })} className="in" /></Fld>
          </div>
          {editing && (
            <div className="flex items-center gap-2 pt-1">
              <input type="checkbox" checked={f.is_active} onChange={e => setF({ ...f, is_active: e.target.checked })} id="coa-active" />
              <label htmlFor="coa-active" className="text-sm text-slate-700">Aktif (uncheck untuk nonaktifkan)</label>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy} data-testid="coa-form-submit" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); } .in:disabled { background: #F8FAFC; color: #94A3B8; }`}</style>
      </div>
    </div>
  );
}

// ============ Kelola kategori akun ============

function KelolaKategori({ reg, onClose }) {
  const [rows, setRows] = useState(reg?.kategori || []);
  const [tambah, setTambah] = useState(false);
  const [busy, setBusy] = useState(false);
  const kelompok = reg?.kelompok || [];

  const muat = async () => {
    try { const r = await api.get("/finance/kategori-akun"); setRows(r.data?.kategori || []); }
    catch { toast.error("Gagal memuat kategori"); }
  };

  const simpan = async (key, patch) => {
    setBusy(true);
    try { await api.put(`/finance/kategori-akun/${key}`, patch); toast.success("Kategori diperbarui ✓"); await muat(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const hapus = async (k) => {
    if (!window.confirm(`Hapus kategori '${k.label}'?`)) return;
    setBusy(true);
    try { await api.delete(`/finance/kategori-akun/${k.key}`); toast.success("Kategori dihapus ✓"); await muat(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="kategori-akun-modal" className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display font-bold text-lg">Kategori Akun COA</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Kategori menentukan di kelompok mana akun muncul di Neraca dan ke arah mana saldonya
          dihitung. <b>Arah normal tidak bisa diubah</b> untuk kategori bawaan maupun kategori yang
          sudah dipakai akun — mengubahnya akan membalik tanda saldo di Neraca tanpa satu pun
          jurnal berubah.
        </p>

        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Kategori</th>
                <th className="text-left px-3 py-2 font-semibold">Kelompok Neraca</th>
                <th className="text-left px-3 py-2 font-semibold">Saldo Normal</th>
                <th className="text-right px-3 py-2 font-semibold">Akun</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(k => (
                <BarisKategori key={k.key} k={k} kelompok={kelompok} busy={busy}
                               onSimpan={simpan} onHapus={hapus} />
              ))}
            </tbody>
          </table>
        </div>

        {tambah
          ? <FormKategoriBaru kelompok={kelompok} onBatal={() => setTambah(false)}
                              onSelesai={async () => { setTambah(false); await muat(); }} />
          : <button onClick={() => setTambah(true)} data-testid="kategori-akun-tambah"
                    className="mt-4 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Kategori Baru
            </button>}
      </div>
    </div>
  );
}

function BarisKategori({ k, kelompok, busy, onSimpan, onHapus }) {
  const [edit, setEdit] = useState(false);
  const [label, setLabel] = useState(k.label);
  const [grup, setGrup] = useState(k.kelompok);

  return (
    <tr data-testid={`kategori-akun-row-${k.key}`} className="hover:bg-slate-50">
      <td className="px-3 py-2">
        {edit
          ? <input value={label} onChange={e => setLabel(e.target.value)} className="w-full h-8 px-2 border border-slate-200 rounded text-sm" />
          : <div>
              <p className="text-slate-900">{k.label}</p>
              <p className="text-[10px] text-slate-400 font-mono">{k.key}</p>
            </div>}
      </td>
      <td className="px-3 py-2">
        {edit
          ? <select value={grup} onChange={e => setGrup(e.target.value)} className="w-full h-8 px-2 border border-slate-200 rounded text-sm">
              {kelompok.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          : <span className="text-slate-700">{k.kelompok}</span>}
      </td>
      <td className="px-3 py-2">
        <span className={`text-xs px-2 py-0.5 rounded-full ${k.arah_normal === "kredit" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
          {k.arah_normal}
        </span>
        {k.arah_terkunci && <Lock className="w-3 h-3 inline-block ml-1.5 text-slate-400" title={k.bawaan ? "Kategori bawaan" : `Dipakai ${k.jumlah_akun} akun`} />}
      </td>
      <td className="px-3 py-2 text-right text-slate-600">{k.jumlah_akun}</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {edit ? (
          <>
            <button disabled={busy} onClick={async () => { await onSimpan(k.key, { label, kelompok: grup }); setEdit(false); }}
                    data-testid={`kategori-akun-simpan-${k.key}`}
                    className="text-xs px-2 py-1 rounded bg-emerald-600 text-white disabled:opacity-50">Simpan</button>
            <button onClick={() => { setEdit(false); setLabel(k.label); setGrup(k.kelompok); }}
                    className="text-xs px-2 py-1 ml-1 rounded border border-slate-200">Batal</button>
          </>
        ) : (
          <>
            <button onClick={() => setEdit(true)} data-testid={`kategori-akun-edit-${k.key}`} title="Ubah label & kelompok"
                    className="w-7 h-7 grid place-items-center inline-grid text-slate-400 hover:text-slate-900 rounded hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button>
            {k.bisa_hapus && (
              <button disabled={busy} onClick={() => onHapus(k)} data-testid={`kategori-akun-hapus-${k.key}`} title="Hapus"
                      className="w-7 h-7 grid place-items-center inline-grid text-slate-400 hover:text-rose-600 rounded hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

function FormKategoriBaru({ kelompok, onBatal, onSelesai }) {
  const [f, setF] = useState({ label: "", kelompok: kelompok[0] || "Aset Lancar", arah_normal: "debit", boleh_transaksi_kas: false });
  const [busy, setBusy] = useState(false);

  const simpan = async () => {
    if (!f.label.trim()) return toast.error("Nama kategori wajib diisi");
    setBusy(true);
    try { await api.post("/finance/kategori-akun", f); toast.success("Kategori dibuat ✓"); onSelesai(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-emerald-200 bg-emerald-50/40 rounded-xl p-4 space-y-3" data-testid="kategori-akun-form-baru">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Fld label="Nama Kategori">
          <input value={f.label} onChange={e => setF({ ...f, label: e.target.value })} placeholder="Contoh: Titipan Pelanggan"
                 data-testid="kategori-akun-label" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white" />
        </Fld>
        <Fld label="Kelompok Neraca">
          <select value={f.kelompok} onChange={e => setF({ ...f, kelompok: e.target.value })}
                  data-testid="kategori-akun-kelompok" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white">
            {kelompok.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </Fld>
        <Fld label="Saldo Normal">
          <select value={f.arah_normal} onChange={e => setF({ ...f, arah_normal: e.target.value })}
                  data-testid="kategori-akun-arah" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white">
            <option value="debit">Debit — aset & beban (bertambah di sisi debit)</option>
            <option value="kredit">Kredit — kewajiban, modal, pendapatan</option>
          </select>
        </Fld>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={f.boleh_transaksi_kas} data-testid="kategori-akun-boleh-kas"
                   onChange={e => setF({ ...f, boleh_transaksi_kas: e.target.checked })} />
            Boleh dipakai membayar / menerima uang
          </label>
        </div>
      </div>
      <p className="text-[11px] text-slate-500">
        Saldo normal dan "boleh dipakai membayar" <b>terkunci permanen</b> begitu ada akun memakai
        kategori ini. Pastikan benar sebelum menyimpan.
      </p>
      <div className="flex gap-2">
        <button onClick={onBatal} className="h-9 px-4 rounded-lg border border-slate-200 bg-white text-sm">Batal</button>
        <button onClick={simpan} disabled={busy} data-testid="kategori-akun-simpan-baru"
                className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
      </div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
